/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Box, Button, Card, CardActions, CardContent, Container, Stack, Typography } from '@mui/material';
import { Done } from '@mui/icons-material';
import { Dialog } from '../common/Dialogs';
import { SUBSCRIPTION_PRICING_INFORMATION } from '../../configs/constant';
import { requestSubscriptionActivation } from '../../apis/subscription';

const DIALOG_INITIAL_STATE = {
    open: false,
    message: '',
};

const STORAGE_KEY = 'subscription_activation_requests';
const COOLDOWN_HOURS = 48;
const COOLDOWN_MS = COOLDOWN_HOURS * 60 * 60 * 1000; // 48 hours in milliseconds

const Pricing = () => {
    const [dialog, setDialog] = useState(DIALOG_INITIAL_STATE);
    const [loading, setLoading] = useState(null);
    const [requestedPlans, setRequestedPlans] = useState({});

    // Load requested plans from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const requests = JSON.parse(stored);
                const now = Date.now();
                const validRequests = {};

                // Filter out expired requests (older than 48 hours)
                Object.keys(requests).forEach((plan) => {
                    const requestTime = requests[plan];
                    const timeElapsed = now - requestTime;
                    if (timeElapsed < COOLDOWN_MS) {
                        validRequests[plan] = requestTime;
                    }
                });

                // Update localStorage if any requests expired
                if (Object.keys(validRequests).length !== Object.keys(requests).length) {
                    if (Object.keys(validRequests).length === 0) {
                        localStorage.removeItem(STORAGE_KEY);
                    } else {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(validRequests));
                    }
                }

                setRequestedPlans(validRequests);
            }
        } catch (error) {
            console.error('Error loading activation requests from localStorage:', error);
        }
    }, []);

    // Helper function to check if a plan is requested and still in cooldown
    const isPlanRequested = useMemo(() => {
        return (plan) => {
            return Object.prototype.hasOwnProperty.call(requestedPlans, plan);
        };
    }, [requestedPlans]);

    const requestActivationHandler = async (plan) => {
        // Immediately disable button and mark as requested
        const now = Date.now();
        const updatedRequests = { ...requestedPlans, [plan]: now };
        setRequestedPlans(updatedRequests);

        // Store in localStorage
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedRequests));
        } catch (error) {
            console.error('Error saving activation request to localStorage:', error);
        }

        try {
            setLoading(plan);
            const plan_lang = plan + ' Plan';
            await requestSubscriptionActivation({ plan_name: plan_lang });
            setDialog({
                ...DIALOG_INITIAL_STATE,
                open: true,
            });
        } catch (error) {
            toast.error('Failed to activate plan');
            // Remove from requested plans if request failed
            const { [plan]: removed, ...rest } = updatedRequests;
            setRequestedPlans(rest);
            try {
                if (Object.keys(rest).length === 0) {
                    localStorage.removeItem(STORAGE_KEY);
                } else {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
                }
            } catch (storageError) {
                console.error('Error updating localStorage after failed request:', storageError);
            }
        } finally {
            setLoading(null);
        }
    };

    return (
        <div className="pricing">
            <ToastContainer />
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 2 } }}>
                <Box variant="div" className="header-container">
                    <Typography className="header-text">Choose Your AI Plan</Typography>
                    <Typography className="sub-text">
                        Scalable pricing designed for every organization size and requirement
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap', lg: 'nowrap' },
                        gap: { xs: 2, sm: 2, md: 2, lg: 1.5 },
                        mt: 3,
                        '& > *': {
                            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)', lg: '1 1 0' },
                            minWidth: 0,
                            maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(50% - 8px)', lg: 'none' },
                        },
                    }}
                >
                    {SUBSCRIPTION_PRICING_INFORMATION?.map((card) => (
                        <Card key={card?.title} className="card-container">
                            <CardContent sx={{ p: '0.875rem !important', '&:last-child': { pb: '0.875rem !important' } }}>
                                <Stack sx={{ mb: 1 }} direction={'column'} justifyContent={'space-around'}>
                                    <div className="text-center">
                                        <Typography className="card-title">{card?.title}</Typography>

                                        <div className="pricing-container">
                                            <Typography className="price">{card?.price}</Typography>
                                            <Typography className="quantity">{card?.priceQuantity}</Typography>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <Details card={card} />

                                    {card?.info?.map((info) => (
                                        <Stack key={info} className="info">
                                            <Done color="primary" />
                                            <Typography key={info}>{info}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                            <CardActions>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => requestActivationHandler(card?.title)}
                                    disabled={loading === card?.title || isPlanRequested(card?.title)}
                                    disableElevation
                                >
                                    {loading === card?.title
                                        ? 'Processing...'
                                        : isPlanRequested(card?.title)
                                        ? 'Requested'
                                        : card?.buttonText}
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </Box>

                <Dialog
                    open={dialog.open}
                    dialogSize="sm"
                    onClose={() => setDialog(DIALOG_INITIAL_STATE)}
                    message={
                        <Stack direction={'column'} gap={2}>
                            <Typography fontSize={22} fontWeight={800}>
                                Thanks for reaching out!
                            </Typography>
                            <Typography fontSize={16}>
                                We’ve received your request and our team will get in touch with you soon.
                            </Typography>
                            <Typography fontSize={16}>
                                You can always contact us at{' '}
                                <a
                                    href="mailto:support@xtract2ai.com"
                                    aria-label="Send an email to support@xtract2ai.com about an inquiry"
                                >
                                    support@xtract2ai.com
                                </a>{' '}
                                for any questions.
                            </Typography>
                        </Stack>
                    }
                    dialogActions={false}
                    dialogTitle={false}
                />
            </Container>
        </div>
    );
};

export default Pricing;

const Details = ({ card }) => (
    <Stack my={2.5} className="space-y-4">
        <div className="details">
            <Typography>Courses Included:</Typography>
            <Typography>{card?.courses}</Typography>
        </div>
        <div className="details">
            <Typography>Upload Quota: </Typography>
            <Typography> {card?.uploadQuota}</Typography>
        </div>
        <div className="details">
            <Typography>Max Package Size: </Typography>
            <Typography>{card?.maxPackageSize}</Typography>
        </div>
        <div className="details">
            <Typography>Media Hours: </Typography>
            <Typography>{card?.mediaHours}</Typography>
        </div>
        <div className="details">
            <Typography>Cost/Course: </Typography>
            <Typography>{card?.costPerCourse}</Typography>
        </div>
    </Stack>
);
